import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from 'src/api/apiClient';
import {
  DELETE_WEBSITE_AGENT_KNOWLEDGE_BASE,
  GET_WEBSITE_AGENT_CONFIG_DETAILS,
  GET_WEBSITE_AGENT_S3_URL,
  SAVE_WEBSITE_AGENT_BASIC_SETUP,
  SAVE_WEBSITE_AGENT_CONVERSATION_SETUP,
  SAVE_WEBSITE_AGENT_GENERAL,
  SAVE_WEBSITE_AGENT_KNOWLEDGE_BASE,
  SAVE_WEBSITE_AGENT_SIGNALS,
} from 'src/constants/httpEndPoints';
import { BasicSetupTypes, ConversationSetupTypes, GeneralTypes, KnowledgeBaseTypes, SignalTypes } from './types';

export const getWebsiteConfigCompanyIndustry = createAsyncThunk(
  'website-agent/getWebsiteAgentCompanyIndustry',
  async (payload: { customerId: string; domain: string }) => {
    const { customerId, domain } = payload;
    try {
      const res = await apiClient.post(
        `https://devmdqs.sprouts.ai/v1/accounts/search`,
        {
          customer_id: customerId,
          page_size: 10,
          sort_by: '',
          sort_order: 'ASC',
          list_type: 'TOTAL',
          filters: [
            {
              filter_name: 'Company',
              filter_key: 'company',
              filter_data: {
                includes: [domain],
                excludes: [],
              },
            },
          ],
        },
        {
          headers: {
            'X-Customer-Id': customerId,
          },
        },
      );
      return res.data?.list?.flatMap(item => item.industries);
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to fetch website agent config details');
    }
  },
);

export const getWebsiteAgentConfigDetails = createAsyncThunk(
  'website-agent/getWebsiteAgentConfigDetails',
  async (payload: { customerId: string; emailId: string }) => {
    const { customerId, emailId } = payload;
    try {
      const res = await apiClient.post(
        `${GET_WEBSITE_AGENT_CONFIG_DETAILS}`,
        {},
        {
          headers: {
            'X-Login-Email': emailId,
            'X-Customer-Id': customerId,
          },
        },
      );
      return res.data;
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to fetch website agent config details');
    }
  },
);
export const reFetchWebsiteAgentConfigDetails = createAsyncThunk(
  'website-agent/reFetchWebsiteAgentConfigDetails',
  async (payload: { customerId: string; emailId: string }) => {
    const { customerId, emailId } = payload;
    try {
      const res = await apiClient.post(
        `${GET_WEBSITE_AGENT_CONFIG_DETAILS}`,
        {},
        {
          headers: {
            'X-Login-Email': emailId,
            'X-Customer-Id': customerId,
          },
        },
      );
      return res.data;
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to fetch website agent config details');
    }
  },
);
export const saveWebsiteAgentGeneral = createAsyncThunk(
  'website-agent/saveWebsiteAgentGeneral',
  async (payload: { customerId: string; emailId: string; generalData: GeneralTypes }) => {
    const { customerId, emailId, generalData } = payload;
    try {
      await apiClient.post(
        `${SAVE_WEBSITE_AGENT_GENERAL}`,
        {
          ...generalData,
        },
        {
          headers: {
            'X-Login-Email': emailId,
            'X-Customer-Id': customerId,
          },
        },
      );
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to save wesbite agent config details');
    }
  },
);

export const saveWebsiteAgentSproutsSignals = createAsyncThunk(
  'website-agent/saveWebsiteAgentSproutsSignals',
  async (payload: { customerId: string; emailId: string; signalData: SignalTypes }) => {
    const { customerId, emailId, signalData } = payload;
    try {
      await apiClient.post(
        `${SAVE_WEBSITE_AGENT_SIGNALS}`,
        {
          ...signalData,
        },
        {
          headers: {
            'X-Login-Email': emailId,
            'X-Customer-Id': customerId,
          },
        },
      );
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to save wesbite agent config details');
    }
  },
);

export const saveWebsiteAgentBasicSetUp = createAsyncThunk(
  'website-agent/saveWebsiteAgentBasicSetUp',
  async (payload: { customerId: string; emailId: string; basicData: BasicSetupTypes }) => {
    const { customerId, emailId, basicData } = payload;
    try {
      await apiClient.post(
        `${SAVE_WEBSITE_AGENT_BASIC_SETUP}`,
        {
          ...basicData,
        },
        {
          headers: {
            'X-Login-Email': emailId,
            'X-Customer-Id': customerId,
          },
        },
      );
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to save wesbite agent config details');
    }
  },
);

export const saveWebsiteAgentKnowledgebase = createAsyncThunk(
  'website-agent/saveWebsiteAgentKnowledgebase',
  async (payload: { customerId: string; emailId: string; knowledgeData: KnowledgeBaseTypes }) => {
    const { customerId, emailId, knowledgeData } = payload;
    try {
      await apiClient.post(
        `${SAVE_WEBSITE_AGENT_KNOWLEDGE_BASE}`,
        {
          ...knowledgeData,
        },
        {
          headers: {
            'X-Login-Email': emailId,
            'X-Customer-Id': customerId,
          },
        },
      );
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to save wesbite agent knowledge details');
    }
  },
);

export const deleteWebsiteAgentKnowledgebase = createAsyncThunk(
  'website-agent/deleteWebsiteAgentKnowledgebase',
  async (payload: { customerId: string; emailId: string; knowledgeData: KnowledgeBaseTypes }) => {
    const { customerId, emailId, knowledgeData } = payload;
    try {
      await apiClient.post(
        `${DELETE_WEBSITE_AGENT_KNOWLEDGE_BASE}`,
        {
          ...knowledgeData,
        },
        {
          headers: {
            'X-Login-Email': emailId,
            'X-Customer-Id': customerId,
          },
        },
      );
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to save wesbite agent knowledge details');
    }
  },
);

export const saveWebsiteAgentConversationSetup = createAsyncThunk(
  'website-agent/saveWebsiteAgentConversationSetup',
  async (payload: { customerId: string; emailId: string; conversationData: ConversationSetupTypes }) => {
    const { customerId, emailId, conversationData } = payload;
    try {
      await apiClient.post(
        `${SAVE_WEBSITE_AGENT_CONVERSATION_SETUP}`,
        {
          ...conversationData,
        },
        {
          headers: {
            'X-Login-Email': emailId,
            'X-Customer-Id': customerId,
          },
        },
      );
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to save wesbite agent conversation details');
    }
  },
);

export const getS3Url = createAsyncThunk(
  'website-agent/getS3Url',
  async (payload: { customerId: string; emailId: string; formData: any }) => {
    const { customerId, emailId, formData } = payload;

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', formData.file);
      
      const res = await apiClient.post(
        `${GET_WEBSITE_AGENT_S3_URL}`,
        formDataToSend,
        {
          headers: {
            'X-Login-Email': emailId,
            'X-Customer-Id': customerId,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return res.data;
    } catch (e: any) {
      throw new Error(e.response?.data?.message || 'Failed to upload file');
    }
  }
);
